import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, isAuthRejection, refreshSession } from "@/lib/api";

const AuthContext = createContext(null);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const fetchMe = async () => {
  for (let i = 0; ; i++) {
    try {
      return (await api.get("/auth/me")).data;
    } catch (e) {
      if (isAuthRejection(e)) {
        await refreshSession();
        return (await api.get("/auth/me")).data;
      }
      if (i >= 3) throw e;
      await wait(1000 * (i + 1));
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const reload = useCallback(async () => {
    try {
      setUser(await fetchMe());
    } catch (e) {
      if (isAuthRejection(e)) setUser(false);
      else setUser((u) => (u === null ? false : u));
    }
  }, []);

  useEffect(() => {
    const onReload = () => reload();
    const onLogout = () => setUser(false);
    reload();
    window.addEventListener("auth:reload", onReload);
    window.addEventListener("auth:logout", onLogout);
    return () => {
      window.removeEventListener("auth:reload", onReload);
      window.removeEventListener("auth:logout", onLogout);
    };
  }, [reload]);

  useEffect(() => {
    if (!user) return undefined;
    const keepAlive = () => refreshSession().catch((e) => { if (isAuthRejection(e)) setUser(false); });
    const onVisible = () => { if (document.visibilityState === "visible") keepAlive(); };
    const timer = setInterval(keepAlive, 10 * 60 * 1000);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const value = useMemo(() => ({
    user,
    reload,
    login: async (email, password) => setUser((await api.post("/auth/login", { email: email.trim(), password })).data),
    register: async (form) => setUser((await api.post("/auth/register", { ...form, email: form.email?.trim() })).data),
    logout: async () => {
      try {
        await api.post("/auth/logout");
      } finally {
        setUser(false);
      }
    },
  }), [user, reload]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
